import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';

const SearchableSelect = ({
    options = [],
    value = '',
    onChange,
    placeholder = 'Select an option',
    label,
    error,
    required = false,
    className = '',
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        if (!isOpen) {
            setSearchTerm('');
            setActiveIndex(-1);
        }
    }, [isOpen]);

    useEffect(() => {
        setActiveIndex(-1);
    }, [searchTerm]);

    // Scroll active item into view
    useEffect(() => {
        if (isOpen && activeIndex >= 0 && listRef.current) {
            const activeItem = listRef.current.children[activeIndex];
            if (activeItem) {
                activeItem.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [activeIndex, isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (activeIndex < filteredOptions.length - 1) {
                    setActiveIndex(prev => prev + 1);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (activeIndex > 0) {
                    setActiveIndex(prev => prev - 1);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[activeIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            case 'Tab':
                setIsOpen(false);
                break;
            default:
                break;
        }
    };

    return (
        <div className={cn("w-full relative", className)} ref={containerRef} onKeyDown={handleKeyDown}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            {/* Select Trigger */}
            <div
                onClick={() => !disabled && setIsOpen(!isOpen)}
                tabIndex={disabled ? -1 : 0}
                className={cn(
                    "relative w-full px-3 py-2 bg-white border rounded-lg cursor-pointer transition-all duration-200 flex items-center justify-between outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500",
                    error ? "border-red-300 ring-1 ring-red-300" : "border-gray-300 hover:border-primary-400",
                    isOpen && !error && "border-primary-500 ring-2 ring-primary-100",
                    disabled && "bg-gray-50 cursor-not-allowed opacity-60"
                )}
            >
                <span className={cn("truncate text-sm", !value && "text-gray-400")}>
                    {value || placeholder}
                </span>
                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <div
                            className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                            onClick={handleClear}
                        >
                            <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-pointer" />
                        </div>
                    )}
                    <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform duration-200", isOpen && "rotate-180")} />
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-[60] mt-1 w-full bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in duration-200 origin-top">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-50 flex items-center bg-gray-50">
                        <Search className="h-4 w-4 text-gray-400 ml-2" />
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full bg-transparent border-none focus:ring-0 text-sm py-1.5 px-3 placeholder-gray-400 outline-none"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                // Prevent input from capturing navigation keys but allow typing
                                if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) {
                                    // Let the container handler deal with it, but don't preventDefault here
                                    // so that Enter doesn't submit forms if present, etc.
                                    // Actually, we want to prevent cursor moving in input for Up/Down
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();

                                    // Stop propagation so the input doesn't handle it, 
                                    // but wait, we need to manually trigger the container handler if we are focused here?
                                    // React events bubble, so container onKeyDown should catch it.
                                }
                            }}
                        />
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto py-1" ref={listRef}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    className={cn(
                                        "px-4 py-2.5 text-sm cursor-pointer transition-colors duration-150",
                                        (value === option || activeIndex === index) ? "bg-primary-50 text-primary-700 font-medium" : "text-gray-700 hover:bg-gray-50"
                                    )}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-6 text-sm text-gray-400 text-center">
                                No results found for "{searchTerm}"
                            </div>
                        )}
                    </div>
                </div>
            )}

            {error && (
                <p className="mt-1 text-xs text-red-500">{error}</p>
            )}
        </div>
    );
};

export default SearchableSelect;
